import React, { useState, useEffect } from "react";
import classes from "./UserControlComp.module.scss";
import SearchBox from "../topnav/searchBox/SearchBox";
import Button from "../UI/button/Button";
import DataGrid from "../datagrid/DataGrid";
import { Edit, Delete, PersonAdd } from "@mui/icons-material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import ContentModal from "../UI/modal/ContentModal";
import AddUser from "../add/addUser/addUser";
import { useDeleteUserMutation, useGetUsersQuery } from "../../api/authApi";
import EditUser from "../edit/editUser/editUser";
import AddRole from "../add/addRole/addRole";
import { useGetEmployeesQuery } from "../../api/masterApi";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import toast from "react-hot-toast";

export interface User {
  user_id: number;
  emp_id?: string;
  email: string;
  username?: string;
  is_active: boolean;
  role_id: number;
  role_type: string;
  can_read: boolean;
  can_write: boolean;
  can_delete: boolean;
}

const UserControlComp: React.FC = () => {
  const [deleteUser] = useDeleteUserMutation();
  const { data: employees } = useGetEmployeesQuery();
  const { data: apiResponse, isLoading, error } = useGetUsersQuery();
  const user = useSelector((state: RootState) => state.auth.user as User | null);

  const [openModal, setOpenModal] = useState(false);
  const [openModal2, setOpenModal2] = useState(false);
  const [openModal3, setOpenModal3] = useState(false);

  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // ✅ Flatten API data into simpler structure
  const flattenUsers = (data: any[]): User[] =>
    data.map((u) => ({
      user_id: u.user_id,
      email: u.email,
      username: u.username,
      emp_id: u.emp_id,
      is_active: u.is_active,
      role_id: u.roles?.role_id,
      role_type: u.roles?.role_type || "N/A",
      can_read: u.roles?.permissions?.can_read ?? false,
      can_write: u.roles?.permissions?.can_write ?? false,
      can_delete: u.roles?.permissions?.can_delete ?? false,
    }));

  useEffect(() => {
    if (apiResponse) {
      const formatted = flattenUsers(apiResponse);
      setFilteredUsers(formatted);
    }
  }, [apiResponse]);

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setOpenModal2(true);
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (userToDelete) {
      try {
        await deleteUser(userToDelete.user_id).unwrap();
        toast.success("User Deleted Successfully");
      } catch {
        toast.error("Failed to delete user");
      }
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    }
  };

  const renderBooleanCell = (params: GridRenderCellParams<User>) => {
    const val = params.value === true;
    return (
      <span style={{ color: val ? "green" : "red", fontWeight: 600 }}>
        {val ? "✔" : "✖"}
      </span>
    );
  };

  const columns: GridColDef<User>[] = [
    { field: "emp_id", headerName: "EMP ID", width: 110 },
    { field: "email", headerName: "E-mail", width: 300 },
    { field: "username", headerName: "Emp Name", width: 200 },
    {
      field: "role_type",
      headerName: "Role Type",
      width: 150,
      renderCell: (params) => (
        <span style={{ fontWeight: 500 }}>{params.value || "N/A"}</span>
      ),
    },
    { field: "can_read", headerName: "Read Access", width: 150, renderCell: renderBooleanCell },
    { field: "can_write", headerName: "Write Access", width: 150, renderCell: renderBooleanCell },
    { field: "can_delete", headerName: "Delete Access", width: 150, renderCell: renderBooleanCell },
    {
      field: "is_active",
      headerName: "Status",
      width: 150,
      renderCell: (params) => {
        const isActive = params.value;
        return (
          <span
            style={{
              backgroundColor: isActive ? "#A5E5D9" : "#F8D7DA",
              color: isActive ? "#000" : "#721C24",
              borderRadius: "15px",
              fontWeight: 500,
              width: "85px",
              height: "30px",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 140,
      renderCell: (params: GridRenderCellParams<User>) => {
        if (params.row.role_type === "Admin") {
          return <span style={{ color: "gray" }}>Restricted</span>;
        }

        const canWrite = user?.can_write;
        const canDelete = user?.can_delete;

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              width: "100%",
            }}
          >
            {canWrite && (
              <div className={classes.iconWrapper}>
                <Edit
                  fontSize="small"
                  onClick={() => handleEditClick(params.row)}
                />
              </div>
            )}
            {canDelete && (
              <div className={classes.iconWrapper}>
                <Delete
                  fontSize="small"
                  onClick={() => handleDeleteClick(params.row)}
                />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  if (isLoading) return <p>Loading...</p>;
  if (error) return <p>Error fetching users</p>;

  return (
    <>
      <div className={classes.usercontrolcomp}>
        <div className={classes.usercontrolcomp__searchPanel}>
          <SearchBox
            data={filteredUsers || []}
            onSearch={setFilteredUsers}
            searchKeys={["user_id", "email", "role_type", "username"]}
          />
          {user?.can_write && (
            <div className={classes.usercontrolcomp__createTabs}>
              <Button onClick={() => setOpenModal3(true)}>
                <PersonAdd style={{ color: "#fff", fontSize: "17px" }} />
                Manage Roles
              </Button>
              <Button onClick={() => setOpenModal(true)}>
                <PersonAdd style={{ color: "#fff", fontSize: "17px" }} />
                Add User
              </Button>
            </div>
          )}
        </div>
        <div className={classes.usercontrolcomp__TableBox}>
          <DataGrid
            rows={filteredUsers}
            columns={columns}
            getRowId={(row) => row.user_id}
            centerFields={[
              "can_read",
              "can_write",
              "can_delete",
              "action",
              "is_active",
            ]}
          />
        </div>
      </div>

      {/* Add User */}
      <ContentModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        content={
          <AddUser
            employees={(employees || []).filter(
              (emp) => !filteredUsers.some((u) => u.email === emp.email)
            )}
            onClose={() => setOpenModal(false)}
          />
        }
      />

      {/* Manage Roles */}
      <ContentModal
        open={openModal3}
        onClose={() => setOpenModal3(false)}
        content={<AddRole onClose={() => setOpenModal3(false)} />}
      />

      {/* Edit User */}
      <ContentModal
        open={openModal2}
        onClose={() => setOpenModal2(false)}
        content={
          selectedUser ? (
            <EditUser user={selectedUser} onClose={() => setOpenModal2(false)} />
          ) : null
        }
      />

      {/* Delete Confirmation */}
      <ContentModal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        content={
          <div style={{ padding: "20px", textAlign: "center" }}>
            <h3>Are you sure?</h3>
            <p>
              Do you really want to delete{" "}
              <strong>{userToDelete?.username || userToDelete?.email}</strong>?
            </p>
            <div
              style={{
                marginTop: "20px",
                display: "flex",
                gap: "10px",
                justifyContent: "center",
              }}
            >
              <Button onClick={() => setDeleteConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteUser}
                style={{ backgroundColor: "#d9534f" }}
              >
                Delete
              </Button>
            </div>
          </div>
        }
      />
    </>
  );
};

export default UserControlComp;
